/**
 * Logger seguro - Não exibe dados sensíveis em produção
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';
  private logHistory: LogEntry[] = [];
  private maxHistory = 100;

  private sanitizeData(data: unknown): unknown {
    if (!data || typeof data !== 'object') return data;

    const sensitiveFields = [
      'password', 'token', 'access', 'refresh', 'key', 'secret',
      'senha', 'cpf', 'creditCard', 'cc', 'bank'
    ];

    const sanitized = { ...data as Record<string, unknown> };

    Object.keys(sanitized).forEach(key => {
      if (sensitiveFields.some(field =>
        key.toLowerCase().includes(field.toLowerCase())
      )) {
        sanitized[key] = '***REDACTED***';
      }
    });

    return sanitized;
  }

  private createLogEntry(level: LogLevel, message: string, context?: Record<string, unknown>): LogEntry {
    return {
      level,
      message,
      timestamp: new Date().toISOString(),
      context: context ? (this.sanitizeData(context) as Record<string, unknown>) : undefined
    };
  }

  private log(level: LogLevel, message: string, context?: Record<string, unknown>): void {
    const entry = this.createLogEntry(level, message, context);

    // Mantém histórico em desenvolvimento
    if (this.isDevelopment) {
      this.logHistory.push(entry);
      if (this.logHistory.length > this.maxHistory) {
        this.logHistory.shift();
      }

      // Exibe apenas em desenvolvimento
      const logMessage = `[${entry.timestamp}] ${level.toUpperCase()}: ${message}`;

      switch (level) {
        case 'error':
          console.error(logMessage, entry.context);
          break;
        case 'warn':
          console.warn(logMessage, entry.context);
          break;
        case 'info':
          console.info(logMessage, entry.context);
          break;
        case 'debug':
          console.log(logMessage, entry.context);
          break;
      }
    } else {
      // Em produção, apenas loga erros (pode ser enviado para serviço de logging)
      if (level === 'error') {
        // Aqui poderia enviar para Sentry, LogRocket, etc.
        console.error('[ERROR]', message);
      }
    }
  }

  debug(message: string, context?: Record<string, unknown>): void {
    this.log('debug', message, context);
  }

  info(message: string, context?: Record<string, unknown>): void {
    this.log('info', message, context);
  }

  warn(message: string, context?: Record<string, unknown>): void {
    this.log('warn', message, context);
  }

  error(message: string, context?: Record<string, unknown>): void {
    this.log('error', message, context);
  }

  // Métodos específicos para segurança
  security(message: string, context?: Record<string, unknown>): void {
    this.warn(`[SECURITY] ${message}`, context);
  }

  auth(message: string, context?: Record<string, unknown>): void {
    this.info(`[AUTH] ${message}`, context);
  }

  api(message: string, context?: Record<string, unknown>): void {
    this.info(`[API] ${message}`, context);
  }

  // Métodos para debugging controlado
  getLogHistory(): LogEntry[] {
    return [...this.logHistory];
  }

  clearLogHistory(): void {
    this.logHistory = [];
  }

  // Validação de ambiente
  isDevMode(): boolean {
    return this.isDevelopment;
  }
}

// Exportar instância única
export const logger = new Logger();

// Exportar tipo para uso em componentes
export type { LogLevel, LogEntry };

// Para debugging rápido (apenas em desenvolvimento)
export const debugLog = (message: string, data?: Record<string, unknown>) => {
  if (process.env.NODE_ENV === 'development') {
    logger.debug(message, data);
  }
};